import { useState, useEffect, useMemo, useCallback } from 'react'
import Image from 'next/image'
import { useScroll, useSpring, useTransform, AnimatePresence, useMotionValue } from 'motion/react'
import * as m from 'motion/react-m'
import { Motion } from '@/lib/motion/lazy-motion'
import Hero from '@/components/content/common/Hero'
import AppPicker from '@/components/ui/AppPicker'
import Progress from '@/components/content/modules/hero/Progress'
import { ReviewHeroContent } from '@/components/content/reviews/review/ReviewHero'
import Pill from '@/components/ui/Pill'
import Link from '@/components/ui/Link'
import { cn, hexToRgba, getBrandColors, filterGoodCategories, isPopulatedObject } from '@/lib/utils'
import { checkAppBranding } from '@/lib/formatting'
import { useDeferredActivation } from '@/hooks/useDeferredActivation'
import { easeLong } from '@/lib/transitions'
import { Classification } from '@/collections/JunctionTables/AppCategoryRecommendation/constants'
import type {
  AppCategoryRecommendation,
  App,
  Review,
  Deal,
  ComparisonAppsApp,
  Alternative,
} from '@/payload-types'

export default function ReviewsHero({ reviews }: { reviews: Review[] }) {
  const [count, setCount] = useState(0)
  const [activeIndex, setActiveIndex] = useState(0)
  const [review, setReview] = useState<Review | null>(reviews[0] || null)

  const apps = reviews.map((review) => review.app as App)
  const app = review?.app as App

  useEffect(() => {
    setReview(reviews[count] || null)
  }, [count, reviews])

  useEffect(() => {
    setCount(activeIndex)
  }, [activeIndex, setCount])

  if (!app) return null

  const { primaryColor, secondaryColor } = getBrandColors(app)

  const gradient = `radial-gradient(70% 100% at 0% 0%, ${hexToRgba(primaryColor, 0.25)} 0%, ${hexToRgba(primaryColor, 0)} 80%),
        radial-gradient(70% 100% at 100% 0%, ${hexToRgba(secondaryColor, 0.25)} 0%, ${hexToRgba(secondaryColor, 0)} 80%),
        radial-gradient(100% 100% at 50% 0%, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0) 80%, rgba(0, 0, 0, 0) 100%),
        #000`

  const { appIcon, appGlyph, appHomepage } = checkAppBranding(app)

  return (
    <Hero gradient={gradient} animation={{ scroll: false }} className="pt-20 md:pt-0">
      {({ animations }) => (
        <>
          <AnimatePresence>
            {review && (
              <FeaturedReview review={review} activeIndex={activeIndex} style={animations} />
            )}
          </AnimatePresence>

          <Visual
            count={count}
            app={app.name}
            icon={appGlyph?.url || appIcon?.url || ''}
            screenshot={appHomepage?.url || ''}
            priority
          />

          <AppPicker
            apps={apps}
            currentIndex={activeIndex}
            setCurrentIndexAction={setActiveIndex}
            direction="vertical"
          />
          <Progress
            total={reviews.length}
            activeIndex={activeIndex}
            setActiveIndex={setActiveIndex}
            autoStart="afterInteraction"
          />
        </>
      )}
    </Hero>
  )
}

export function FeaturedReview({
  review,
  activeIndex,
  style,
}: {
  review: Review
  activeIndex: number
  style?: any
}) {
  const app = review.app as App
  const deals = app.deals?.docs as Deal[]
  const comparisons = app.comparisonsApps?.docs as ComparisonAppsApp[]
  const alternatives = app.alternatives?.docs as Alternative[]

  const dealCount = deals?.length ?? 0
  const comparisonCount = comparisons?.length ?? 0
  const alternativesCount = alternatives?.length ?? 0

  const goodCategories = filterGoodCategories(
    (app.categoryRecommendations?.docs ?? []).filter((rec): rec is AppCategoryRecommendation =>
      isPopulatedObject<AppCategoryRecommendation>(rec),
    ),
  )

  const awardCategory =
    goodCategories.length > 0
      ? goodCategories.find((c) => c.classification === Classification.Primary)
      : undefined

  return (
    <Motion>
      <m.div
        key={`${review.id}-${activeIndex}`}
        initial={{ opacity: 0, y: '50px', x: '-50%' }}
        animate={{ opacity: 1, y: '0px', x: '-50%' }}
        exit={{ opacity: 0, y: '50px', x: '-50%' }}
        transition={easeLong}
        className="absolute bottom-8 left-1/2 z-20 flex w-full flex-col items-center gap-8 px-10 text-center"
        style={style}
      >
        <ReviewHeroContent
          app={app}
          reviewId={review.id}
          category={awardCategory}
          reviewCriterionRatings={review.reviewCategoryCriterionRating?.docs}
          root={true}
        />

        <div className="flex flex-col items-center gap-6">
          <Link key={`link-${activeIndex}`} href={`/apps/${app.slug}`}>
            <Pill key={`pill-${activeIndex}`} size="large" style="gradient" className="w-full">
              Read {app.name} Review
            </Pill>
          </Link>
          <p className="text-center text-12 text-white/60">
            {dealCount} deal{dealCount === 1 ? '' : 's'}, {comparisonCount} comparison
            {comparisonCount === 1 ? '' : 's'}, {alternativesCount} alternative
            {alternativesCount === 1 ? '' : 's'}
          </p>
        </div>
      </m.div>
    </Motion>
  )
}

export function Visual({
  count,
  app,
  icon,
  screenshot,
  priority,
}: {
  count: number
  app: string
  icon: string
  screenshot?: string
  priority?: boolean
}) {
  const { ref, active: interactive } = useDeferredActivation({
    strategy: 'after-interaction-idle',
  })
  const height = 680
  const { scrollY } = useScroll()
  const springConfig = { damping: 20, stiffness: 200 }

  const logoX = useMotionValue(0)
  const logoY = useMotionValue(0)
  const logoSpringX = useSpring(logoX, springConfig)
  const logoSpringY = useSpring(logoY, springConfig)

  const screenX = useMotionValue(0)
  const screenY = useMotionValue(0)
  const screenSpringX = useSpring(screenX, springConfig)
  const screenSpringY = useSpring(screenY, springConfig)

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      const { innerWidth, innerHeight } = window
      const { clientX, clientY } = e

      const relativeX = clientX - innerWidth / 2
      const relativeY = clientY - innerHeight / 2

      logoX.set(relativeX / 20)
      logoY.set(relativeY / 20)
      screenX.set(relativeX / 60)
      screenY.set(relativeY / 60)
    },
    [logoX, logoY, screenX, screenY],
  )

  useEffect(() => {
    if (!interactive) return
    window.addEventListener('mousemove', handleMouseMove, { passive: true })

    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [interactive, handleMouseMove])

  const scrollGlyph = useTransform(scrollY, [0, height], [0, -50])
  const scrollScreen = useTransform(scrollY, [0, height], [0, -150])
  const opacity = useTransform(scrollY, [0, height], [1, 0])

  const screenMask = useMemo(
    () => `radial-gradient(ellipse 65% 75% at 35% 25%, white 30%, rgba(255,255,255,0) 100%)`,
    [],
  )

  const glyphMask = useMemo(
    () =>
      `linear-gradient(60deg, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 50%, rgba(255,255,255,0) 100%)`,
    [],
  )

  function ScreenLayer({
    index,
    zIndex,
    translateY = 0,
    image,
  }: {
    index: number
    zIndex: number | string
    translateY?: number
    image?: string
  }) {
    return (
      <m.div
        key={`screen-${index}`}
        initial={
          interactive
            ? {
                opacity: 0,
                y: `${translateY + 50}px`,
                skewX: '18deg',
                skewY: '12deg',
                rotate: '-24deg',
              }
            : false
        }
        animate={{
          opacity: 1,
          y: `${translateY}px`,
          skewX: '18deg',
          skewY: '12deg',
          rotate: '-24deg',
        }}
        exit={{
          opacity: 0,
          y: `${translateY - 50}px`,
          skewX: '18deg',
          skewY: '12deg',
          rotate: '-24deg',
        }}
        transition={{ duration: 0.5, delay: index * 0.1 }}
        className={cn(
          'backface-hidden absolute left-0 h-full w-full overflow-hidden rounded-2xl bg-gray-950 shadow-[0px_50px_100px_-25px_rgba(0,0,0,1)] after:absolute after:left-0 after:top-0 after:h-full after:w-full after:rounded-2xl after:border after:border-white after:border-opacity-20',
        )}
        style={{
          maskImage: screenMask,
          WebkitMaskImage: screenMask,
          zIndex,
        }}
      >
        {image && (
          <Image
            src={image}
            alt={`${app} screenshot`}
            fill
            priority={priority}
            sizes="(min-width: 1536px) 1080px, (min-width: 1024px) 828px, 80vw"
            className="object-cover object-left-top"
          />
        )}
      </m.div>
    )
  }

  return (
    <Motion>
      <m.div
        ref={ref}
        initial={false}
        style={interactive ? { opacity } : undefined}
        className="absolute left-[10%] top-20 z-10 mx-auto aspect-screen w-[80%] max-w-screen-md md:bottom-[20%] lg:left-1/2 lg:top-auto lg:w-[60%] lg:-translate-x-1/2"
      >
        <AnimatePresence>
          <div className="relative h-full w-full scale-[1]">
            <m.div
              key={`glyph-${count}`}
              initial={interactive ? { opacity: 0, scale: 0.8 } : false}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.3, delay: 0.5 }}
              className="absolute left-[85%] top-[40%] z-30 aspect-square w-[33%]"
              style={
                interactive
                  ? {
                      y: scrollGlyph,
                      x: logoSpringX,
                      translateY: logoSpringY,
                    }
                  : undefined
              }
            >
              <div
                style={{
                  backgroundImage: icon ? `url(${icon})` : 'none',
                  WebkitMaskImage: icon ? glyphMask : 'none',
                  maskImage: icon ? glyphMask : 'none',
                }}
                className="backface-hidden absolute h-full w-full -rotate-6 skew-x-6 bg-cover bg-no-repeat"
              />
            </m.div>

            <m.div
              className="absolute left-0 top-0 z-20 h-full w-full"
              style={
                interactive
                  ? {
                      y: scrollScreen,
                      x: screenSpringX,
                      translateY: screenSpringY,
                    }
                  : undefined
              }
            >
              <ScreenLayer index={4} zIndex={4} image={screenshot} />
              {[3, 2, 1, 0].map((index) => (
                <ScreenLayer
                  key={index}
                  index={index}
                  zIndex={index}
                  translateY={(4 - index) * 50}
                />
              ))}
            </m.div>
          </div>
        </AnimatePresence>
      </m.div>
    </Motion>
  )
}
